import {
  BaseEdge as RFBaseEdge,
  EdgeLabelRenderer,
  getStraightPath,
  useReactFlow,
  useStore,
  type EdgeProps,
} from '@xyflow/react'
import type { BaseEdgeComponentData, BaseEdgeColorConfig } from '../../types'
import { DEFAULT_NODE_SIZE, DEFAULT_ZOOM_THRESHOLD, EDGE_COLORS } from '../../constants'
import { getEdgeConnectionPoints } from '../../utils/edge-utils'
import styles from './base-edge.module.scss'

const ARROW_MARKER_ID = 'edge-arrow-marker'
const ARROW_MARKER_URL = `url(#${ARROW_MARKER_ID})`
const LABEL_OPACITY_BOOST = 0.2

/** Arrow marker that inherits color from edge stroke via context-stroke */
function ArrowMarkerDef() {
  return (
    <marker
      id={ARROW_MARKER_ID}
      markerWidth='10'
      markerHeight='10'
      viewBox='-10 -10 20 20'
      markerUnits='strokeWidth'
      orient='auto-start-reverse'
      refX='0'
      refY='0'
    >
      <polyline points='-5,-4 0,0 -5,4 -5,-4' fill='context-stroke' stroke='context-stroke' strokeWidth='1' />
    </marker>
  )
}

export interface BaseEdgeProps extends EdgeProps {
  /** Size of the connected nodes (for calculating connection points) */
  nodeSize?: number
  /** Color configuration for edge states */
  colors?: BaseEdgeColorConfig
  /** Function to resolve label from edge data */
  labelResolver?: (data: BaseEdgeComponentData | undefined) => string
  /** Zoom threshold above which labels are always visible */
  zoomThreshold?: number
}

/**
 * BaseEdge - Generic edge component with zoom-based label visibility and state-aware styling.
 * Calculates connection points on circular node circumferences.
 */
export function BaseEdge({
  id,
  source,
  target,
  data,
  selected,
  nodeSize = DEFAULT_NODE_SIZE,
  colors: colorsProp,
  labelResolver,
  zoomThreshold = DEFAULT_ZOOM_THRESHOLD,
}: BaseEdgeProps) {
  const { getNode } = useReactFlow()
  const zoom = useStore(state => state.transform[2])
  const sourceNode = getNode(source)
  const targetNode = getNode(target)

  if (!sourceNode || !targetNode) {
    return null
  }

  const edgeData = data as BaseEdgeComponentData | undefined
  const isDisabled = edgeData?.disabled || false
  const isReadonly = edgeData?.readonly || false
  const label = labelResolver?.(edgeData) ?? edgeData?.label ?? ''
  const showLabel = (selected || zoom >= zoomThreshold) && !!label

  const colors = { ...EDGE_COLORS, ...colorsProp }

  // Determine edge color and opacity based on state
  let edgeColor: string = colors.default
  let edgeOpacity = 1
  let labelOpacity = 1
  let strokeWidth = 2

  if (selected) {
    edgeColor = colors.selected
    strokeWidth = 2.5
  } else if (isReadonly) {
    edgeOpacity = colors.readonlyOpacity
    labelOpacity = colors.readonlyOpacity + LABEL_OPACITY_BOOST
  } else if (isDisabled) {
    edgeColor = colors.disabled
    labelOpacity = colors.disabledOpacity
  }

  const { sourceX, sourceY, targetX, targetY } = getEdgeConnectionPoints(sourceNode, targetNode, nodeSize)

  const [edgePath, labelX, labelY] = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  })

  return (
    <>
      <defs>
        <ArrowMarkerDef />
      </defs>
      <RFBaseEdge
        id={id}
        path={edgePath}
        markerEnd={ARROW_MARKER_URL}
        style={{
          stroke: edgeColor,
          strokeWidth,
          opacity: edgeOpacity,
        }}
        interactionWidth={22}
      />
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            className={styles.edgeLabel}
            style={{
              left: labelX,
              top: labelY,
              fontSize: 10,
              lineHeight: '20px',
              color: selected ? colors.selected : 'rgba(0, 0, 0, 0.45)',
              opacity: labelOpacity,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}
